import React, { useEffect, useRef, useState } from 'react';
import './ApplicantPage.scss';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';
import {
  getFilterData,
  getJsonFromStorage,
  getPaginationOpts, getResponseError,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import Pagination from '../../../components/base/Pagination/Pagination';
import CustomizedGrid from '../../../components/base/Grid/CustomizedGrid';
import { APPLICANT_MODULE } from '../../../configs/moduleNames';
import { applicantColumn } from './applicantColumns';
import FilterRow from '../../../components/base/FilterRow/FilterRow';
import FieldDTO from '../../../components/fields/Abstract/FieldDTO';
import { Icons, Button } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import TextInput from '../../../components/fields/TextInput/TextInput';
import {
  APPLICANT_ROLES_LIST,
  APPLICANT_STATUS_LIST,
  USER_TYPE_SHORT_NAME_LIST,
} from '../../../configs';
import getDefaultSort from '../../../utils/getDefaultSort';
import FilterModal from '../../FilterTemplate/components/FilterModal';
import { PAGE_TYPE } from '../../FilterTemplate/components/filter-settings';
import { deleteApplicant, loadApplicantById, loadApplicantList } from '../actions';
import { getApplicantData } from '../reducer';
import CustomSelectInput from '../../../components/fields/SelectInput/CustomSelectInput';
import TooltipWithPortal from '../../../components/base/Tooltip/TooltipWithPortal';
import { showGlobalConfirm } from '../../System/actions';
import { errorNotification, successNotification } from '../../Notifications/notificationDecorator';
import { ApplicantDetails } from './ApplicantDetails';
import ApplicantSelectType from '../ApplicantSelectType';
import ApplicantDTO from '../../../dto/ApplicantDTO';
import ApplicantFormMiniversion from './ApplicantFormMini';
import TimerLoader from '../../../components/base/Loader/TimerLoader';
import { closeAllPopupWrapper } from '../../InformationScreenWrapper/actions';
import ExportInterface from '../../System/components/ExportInterface';
import TextSearchInput from '../../../components/fields/TextInput/TextSearchInput';

export default function ApplicantPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  const [editForm, setEditForm] = useState(false);
  const store = useStore();
  const [selectedLegalEntityType, setSelectedLegalEntityType] = useState(null);
  const [showSelectProcedure, setShowSelectProcedure] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);
  const {
    applicantList,
    applicantListMeta,
    loadingApplicantList,
    currentApplicant,
  } = useSelector(getApplicantData);
  const filterName = 'applicant-filter';
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [selectedRow, setSelectedRow] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const applicant_id = searchParams.get('applicant_id');
  const applicantDTO = new ApplicantDTO();
  const applicantAttributes = applicantDTO.getAttributesByName(false);

  const { formatMessage } = useIntl();

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[APPLICANT_MODULE].applicantListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: getValues(),
      sort,
    };
    const p1 = dispatch(loadApplicantList(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);

  useEffect(() => () => dispatch((closeAllPopupWrapper())), []);

  const loadApplicant = () => {
    if (applicant_id) {
      dispatch(closeAllPopupWrapper());
      dispatch(loadApplicantById(applicant_id)).catch(response => errorNotification('Загрузка заявителей', getResponseError(response)));
    }
  };

  const handleShow = item => event => {
    if (!item?.id) {
      return;
    }
    setSelectedRow(item);
    setSearchParams({ applicant_id: item?.id });
  };

  const handleShowEdit = item => event => {
    if (!item) {
      return;
    }

    dispatch(loadApplicantById(item.id))
      .then(() => {
        setEditForm(true);
        setShowForm(true);
      })
      .catch(response => {
        errorNotification('Загрузка заявителя', getResponseError(response));
      });
  };

  const handleDelete = item => event => {
    if (!item?.id) {
      return;
    }
    dispatch(showGlobalConfirm({
      confirmMessage: <div className="f2">Удалить карточку заявителя — {item?.full_name || ''}</div>,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteApplicant(item.id)).then(() => {
        loadData();
        successNotification('Удаление карточки заявителя', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление карточки заявителя', getResponseError(response));
      });
    }).catch(() => {});
    return false;
  };

  const rowClassFormatter = item => {
    const classes = ['clickable-table-row'];
    return classes.join(' ');
  };

  useEffect(() => {
    if (applicant_id) {
      setSelectedRow({ id: applicant_id });
    }
  }, [applicant_id]);

  useEffect(loadApplicant, [applicant_id]);

  const renderContextMenu = item => (
    <div className="more-wrapper">
      <Button variant="link" className="w-100 more-button" onClick={handleShow(item)}>
        Посмотреть
      </Button>
      <Button variant="link" className="w-100 more-button" onClick={handleShowEdit(item)}>
        Редактировать
      </Button>
      <Button variant="link" className="w-100 more-button delete" onClick={handleDelete(item)}>
        Удалить
      </Button>
    </div>
  );

  const moreFormatter = (_, item) => (
    <TooltipWithPortal
      content={<Icons customIcon icon="more-vert" className="more-vert" />}
      classNamePortal="more-actions"
      tooltipContent={renderContextMenu(item)}
      classNameWrapper="more-tooltip-wrapper"
      className="more-tooltip"
    />
  );

  useEffect(() => reset(getFilterData(filterName, {})), []);

  const {
    getValues, watch, control, reset,
  } = useForm({ defaultValues: getJsonFromStorage('applicant-page-filter', {}) });

  useEffect(() => {
    const subscription = watch(values => {
      localStorage.setItem(filterName, JSON.stringify(values));
      loadData({ page: 1 });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const [sort, setSort] = useState(getDefaultSort(applicantColumn));

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };

  const handleClose = () => {
    setSelectedRow(false);
    setShowForm(false);
    setEditForm(false);
    setShowSelectProcedure(false);
    setSearchParams({});
  };

  const handleCreate = () => {
    setShowSelectProcedure(true);
    // clearCurrentState();
  };

  useEffect(() => { loadData(); }, [sort]);

  return (
    <div className="page applicant-page">
      <TimerLoader global loading={loadingApplicantList} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.applicants' })}</div>
        <FilterRow>
          <FieldDTO
            control={control}
            component={TextSearchInput}
            dtoObject={{
              name: 'search',
              type: String,
              labelKey: 'search',
            }}
            className="no-border"
          />
          <FieldDTO
            control={control}
            type="text"
            component={CustomSelectInput}
            dtoObject={applicantAttributes.legal_entity_type}
            className="no-border"
            placeholder="Тип заявителя"
            usePortal
            options={USER_TYPE_SHORT_NAME_LIST}
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={applicantAttributes.status}
            usePortal
            placeholder="Статус"
            className="no-border"
            options={APPLICANT_STATUS_LIST}
            addPlaceholder
          />
          <FieldDTO
            control={control}
            component={CustomSelectInput}
            dtoObject={{
              name: 'roles',
              type: String,
              labelKey: 'applicant_role',
            }}
            usePortal
            placeholder="Роль заявителя"
            className="no-border"
            options={APPLICANT_ROLES_LIST}
            addPlaceholder
          />
          <div className="filter-sep" />
          <Button
            variant="second"
          >
            <Icons
              customIcon
              className="file-download"
              width={24}
              height={24}
              icon="import"
            />
          </Button>
          <div className="filter-sep" />
          <ExportInterface page="dict_applicants" exporter="dict_applicants" getValues={getValues} />
          <div className="filter-sep" />
          <Button variant="second" onClick={() => handleCreate()}>
            <Icons
              customIcon
              className="file-download"
              width={24}
              height={24}
              icon="plus"
            />
          </Button>
          <div className="filter-sep" />
          <Button variant="second" onClick={() => setOpenFilter(true)}>
            <Icons customIcon className="file-download" width={24} height={24} icon="filter" />
          </Button>
          {isOpenFilter
            ? (
              <FilterModal
                control={control}
                onClose={() => setOpenFilter(false)}
                getValues={getValues}
                reset={reset}
                defaultValues={{}}
                page={PAGE_TYPE.APPLICANT}
              />
            )
            : null}
        </FilterRow>
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="grid-wrapper frame">
            <div className="custom-scroll-theme sticky-table-wrapper">
              <CustomizedGrid
                data={applicantList}
                rowClassFormatter={rowClassFormatter}
                columns={applicantColumn}
                name="applicant-list"
                onDoubleClickByRow={(item, event) => handleShow(item)(event)}
                changeSort={changeSort}
                sort={sort}
                settingsFormatter={moreFormatter}
                renderContextMenu={renderContextMenu}
              />
            </div>
          </div>
          <Pagination meta={applicantListMeta} list={applicantList} handleChangeMeta={loadData} />
          {selectedRow ? <ApplicantDetails onClose={() => handleClose()} /> : null}
          {showForm ? (
            <ApplicantFormMiniversion
              key={selectedLegalEntityType}
              currentApplicant={currentApplicant}
              isEdit={editForm}
              loadData={loadData}
              selectedLegalEntityType={selectedLegalEntityType}
              onClose={handleClose}
            />
          ) : null}
          {showSelectProcedure ? (
            <ApplicantSelectType
              onSelectType={type => {
                setShowForm(true);
                setShowSelectProcedure(false);
                setSelectedLegalEntityType(type);
              }}
              onClose={() => handleClose()}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
